import { useEffect, useRef, useState } from 'react';

/**
 * Pijltjes om door rijen te bladeren met de muis (zoals Netflix). Eén paar knoppen voor de hele app: ze
 * verschijnen over de rand van de rij waar de muis boven staat, alleen als er die kant op nog iets is.
 * Een klik schuift één scherm verder; de half zichtbare tegel komt vooraan, nooit half afgesneden.
 * Rechtsboven streepjes die laten zien waar je in de rij bent. Alleen op apparaten met een muis:
 * aanraken, de tv-browser en het toetsenbord werken zoals altijd.
 */

interface View { top: number; height: number; left: number; right: number; canLeft: boolean; canRight: boolean; pages: number; page: number }

const FINE = '(hover: hover) and (pointer: fine)';

function measure(el: HTMLElement): View {
  const r = el.getBoundingClientRect();
  const max = el.scrollWidth - el.clientWidth;
  const pages = max > 4 ? Math.max(2, Math.ceil(el.scrollWidth / el.clientWidth)) : 1;
  return {
    top: r.top,
    height: r.height,
    left: r.left,
    right: r.right,
    canLeft: el.scrollLeft > 4,
    canRight: el.scrollLeft < max - 4,
    pages,
    page: max > 0 ? Math.round((el.scrollLeft / max) * (pages - 1)) : 0,
  };
}

/** Hoeveel er geschoven moet worden zodat de rand netjes op een tegel valt. */
function stepFor(el: HTMLElement, dir: 1 | -1): number {
  const r = el.getBoundingClientRect();
  const pad = parseFloat(getComputedStyle(el).scrollPaddingInlineStart) || 0;
  const start = r.left + pad;
  const width = el.clientWidth - pad * 2;
  const kids = [...el.children].map((c) => c.getBoundingClientRect()).filter((b) => b.width > 0);
  if (dir === 1) {
    // De eerste tegel die rechts (deels) buiten beeld valt, komt vooraan
    const next = kids.find((b) => b.right > r.right - pad + 1);
    const d = next ? next.left - start : 0;
    return d > 8 ? d : width * 0.85;
  }
  // Terug: de eerste tegel die nu helemaal in beeld is, komt achteraan te staan
  const first = kids.find((b) => b.left >= start - 1);
  if (!first) return -width * 0.85;
  const edge = first.right - width;
  const target = kids.find((b) => b.left >= edge - 1);
  const d = target ? target.left - start : -width * 0.85;
  return d < -8 ? d : -width * 0.85;
}

export function RowArrows() {
  const [fine, setFine] = useState(() => typeof window !== 'undefined' && window.matchMedia(FINE).matches);
  const [view, setView] = useState<View | null>(null);
  const track = useRef<HTMLElement | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | undefined>(undefined);
  const frame = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia(FINE);
    const on = () => setFine(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    if (!fine) return;
    const refresh = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        const el = track.current;
        if (!el || !el.isConnected) { track.current = null; setView(null); return; }
        const v = measure(el);
        setView(v.pages > 1 ? v : null);
      });
    };
    const attach = (el: HTMLElement | null) => {
      if (track.current === el) return;
      track.current?.removeEventListener('scroll', refresh);
      track.current = el;
      el?.addEventListener('scroll', refresh, { passive: true });
      refresh();
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const t = e.target as Element | null;
      if (t && box.current?.contains(t)) { window.clearTimeout(hideTimer.current); return; }
      const el = (t?.closest?.('.row-track') as HTMLElement | null) ?? null;
      if (el) {
        window.clearTimeout(hideTimer.current);
        attach(el);
      } else if (track.current) {
        window.clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(() => attach(null), 180);
      }
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', refresh, { passive: true, capture: true });
    window.addEventListener('resize', refresh, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', refresh, { capture: true });
      window.removeEventListener('resize', refresh);
      track.current?.removeEventListener('scroll', refresh);
      track.current = null;
      window.clearTimeout(hideTimer.current);
      cancelAnimationFrame(frame.current);
    };
  }, [fine]);

  if (!fine || !view) return null;

  const go = (dir: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: stepFor(el, dir), behavior: reduce ? 'auto' : 'smooth' });
  };

  const side = { top: view.top, height: view.height };
  return (
    <div ref={box} className="row-arrows" aria-hidden="true">
      {view.canLeft && (
        <button type="button" tabIndex={-1} className="row-arrow is-left" style={{ ...side, left: view.left }} onClick={() => go(-1)}>
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      )}
      {view.canRight && (
        <button type="button" tabIndex={-1} className="row-arrow is-right" style={{ ...side, left: view.right }} onClick={() => go(1)}>
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      )}
      <div className="row-pages" style={{ top: view.top - 10, left: view.right }}>
        {Array.from({ length: Math.min(view.pages, 12) }, (_, i) => <i key={i} className={i === Math.min(view.page, 11) ? 'is-on' : ''} />)}
      </div>
    </div>
  );
}
